import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react'
import { cx } from './cx'
import { useViewportSegments } from '../../layout/segments'
import { registerPaneLayout } from '../../layout/panes'

export interface PaneLayoutProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  variant: 'supporting' | 'list-detail' | 'split'
  /** Pane flessibile: contenuto principale o dettaglio. */
  main: ReactNode
  /** Supporting pane, elenco (list-detail) o seconda meta' (split). */
  pane: ReactNode
  /** Default: supporting 'end'; list-detail e split 'start'. */
  paneSide?: 'start' | 'end' | undefined
  mainLabel?: string | undefined
  paneLabel?: string | undefined
}

/**
 * Canonical layouts M3 (supporting pane, list-detail, split). Le colonne le decide la larghezza del
 * contenitore (container "md-panes"), non il viewport:
 * - sotto 696px: una colonna, nell'ordine in cui compaiono nel DOM (l'ordine del focus e' quello visivo);
 * - da 696px: pane fissa 360 + 24 + flessibile (split: 50/50); da 856px la pane fissa e' 412;
 * - con due segmenti affiancati: sempre due colonne, la prima termina sulla piega; con due segmenti
 *   uno sopra l'altro (tabletop): impilate.
 */
export function PaneLayout({ variant, main, pane, paneSide, mainLabel, paneLabel, className, style, ...rest }: PaneLayoutProps) {
  const side = paneSide ?? (variant === 'supporting' ? 'end' : 'start')
  const rootRef = useRef<HTMLDivElement | null>(null)
  const vs = useViewportSegments()
  const [firstWidth, setFirstWidth] = useState<number | null>(null)

  useEffect(() => registerPaneLayout(), [])

  // Allineamento alla piega: la prima colonna termina su `hinge-start` (meno 12 se la cerniera e' < 24).
  const aligned = vs.axis === 'horizontal' && vs.hinge !== null
  const hingeLeft = vs.hinge?.left ?? 0
  const hingeSize = vs.hinge?.width ?? 0
  useLayoutEffect(() => {
    const el = rootRef.current
    if (!aligned || !el) {
      setFirstWidth(null)
      return undefined
    }
    const measure = () => {
      const left = el.getBoundingClientRect().left
      const end = hingeSize < 24 ? hingeLeft - 12 : hingeLeft
      setFirstWidth(Math.max(120, Math.round(end - left)))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    return () => { ro.disconnect(); window.removeEventListener('resize', measure) }
  }, [aligned, hingeLeft, hingeSize])

  const mainNode = (
    <section key="main" className="md-pane md-pane--main" aria-label={mainLabel}>{main}</section>
  )
  const paneNode = (
    <section key="pane" className="md-pane md-pane--side" aria-label={paneLabel}>{pane}</section>
  )
  const ordered = side === 'start' ? [paneNode, mainNode] : [mainNode, paneNode]
  const s = (firstWidth !== null ? { ...style, '--_a': `${firstWidth}px` } : style) as CSSProperties | undefined
  return (
    <div
      {...rest}
      ref={rootRef}
      className={cx('md-pane-layout', className)}
      data-variant={variant}
      data-side={side}
      data-aligned={aligned ? 'true' : undefined}
      style={s}
    >
      <div className="md-pane-layout__grid">{ordered}</div>
    </div>
  )
}
